import { useEffect, useMemo, useRef, useState } from "react";
import { createForm } from "@formily/core";
import { createSchemaField } from "@formily/react";
import {
  Form,
  FormItem,
  DatePicker,
  Checkbox,
  Cascader,
  Editable,
  Input,
  NumberPicker,
  Switch,
  Password,
  PreviewText,
  Radio,
  Reset,
  Select,
  Space,
  Submit,
  TimePicker,
  Transfer,
  TreeSelect,
  FormGrid,
  FormLayout,
  FormTab,
  FormCollapse,
  ArrayTable,
  ArrayCards,
} from "@formily/antd-v5";
import { message, Button, Col, Row, Card, UploadFile } from "antd";
import { ApiService, SystemFormExtraActionsEnum } from "../../api";
import { useTranslation } from "react-i18next";
import { openNotificationWithIcon, DOMAIN, getAccessToken } from "../../constants";
import { t } from "i18next";
import ButtonGroup from "antd/es/button/button-group";
import NearbyWorksModal from "../../components/NearbyWorksModal";
import UserSelect from "../../components/UserSelect";
import {
  collectDefaults,
  currentProfile,
  withDefaults,
} from "../../components/formdesign/defaults";
import EntitySelect from "../../components/EntitySelect";
import UploadWithCamera from "../../components/UploadWithCamera";
import { downloadFile } from "../../api/downloadFile";
import { apiErrorMessage } from "../../api/apiError";
import RichTextInput from "../../components/RichTextInput";
import MarkdownView from "../../components/markdown/MarkdownView";

// A block of formatted guidance an administrator wrote into the form (C12).
// Renders rather than asks: it holds no answer and stores nothing, which is why
// it is a layout widget in the designer and not a field.
const Markdown = ({ content, style }: any) => (
  <MarkdownView value={content} style={style} />
);

// Honors `mode` (as the tag name) and `style` so schema-defined section headings
// render bold/large instead of as a bare inline span.
const Text = ({ content, mode, style }: any) => {
  if (mode && mode !== "normal" && typeof mode === "string") {
    const Tag = mode as keyof JSX.IntrinsicElements;
    return <Tag style={style}>{content}</Tag>;
  }
  return <span style={style}>{content}</span>;
};

const ATTACHMENT_ACTION = `${DOMAIN}/api/main/attachments/`;

/**
 * Save an attachment to disk instead of navigating to it.
 *
 * antd renders each uploaded item as a bare `<a href={url} target="_blank">`, so
 * a click merely opens the media URL: the browser previews what it can render
 * and flashes a tab shut for what it cannot. Supplying `onPreview` makes antd
 * `preventDefault()` and hand us the file, and `downloadFile` streams the bytes
 * into an anchor carrying a `download` attribute.
 */
const downloadAttachment = async (file: UploadFile) => {
  // A file uploaded moments ago has no `url` yet — only the POST response does.
  const url: string | undefined = file.url ?? file.response?.file;
  if (!url) return;
  try {
    await downloadFile(httpsIfPageIs(url), file.name);
  } catch (error) {
    message.error(error instanceof Error ? error.message : t("Download failed"));
  }
};

/**
 * Safety net against an http:// attachment URL on an https:// page, which the
 * browser blocks outright as mixed content. The real fix is server-side —
 * SECURE_PROXY_SSL_HEADER plus nginx sending X-Forwarded-Proto, see
 * config/settings.py — but if that header ever goes missing the download should
 * degrade to working rather than to a console error. Upgrading is safe: on an
 * https page the http request could not have succeeded anyway.
 */
const httpsIfPageIs = (url: string) =>
  window.location.protocol === "https:" && url.startsWith("http://")
    ? url.replace(/^http:/, "https:")
    : url;

/** Components whose options the seed lays out as a CSS grid. */
const OPTION_GROUPS = ["Checkbox.Group", "Radio.Group"];

/**
 * ArrayTable sub-components that act on a row instead of showing anything.
 * `ArrayTable.Index` is deliberately absent — a row number is content.
 */
const ARRAY_OPERATIONS = [
  "ArrayTable.Remove",
  "ArrayTable.MoveUp",
  "ArrayTable.MoveDown",
  "ArrayTable.SortHandle",
  "ArrayTable.Addition",
];

/** Every `x-component` in a subtree, the node's own first. */
const componentsIn = (node: any, out: string[] = []): string[] => {
  if (!node || typeof node !== "object") return out;
  if (typeof node["x-component"] === "string") out.push(node["x-component"]);
  if (node.properties && typeof node.properties === "object") {
    Object.values(node.properties).forEach((child) => componentsIn(child, out));
  }
  if (node.items) componentsIn(node.items, out);
  return out;
};

/**
 * Drop an ArrayTable's operations column when the form is read-only.
 *
 * Formily renders `ArrayTable.Remove` (and the Add-row button) as `null` outside
 * `editable` pattern — but only the *control* disappears. The column it sits in
 * is still declared, so a submitted table prints a headerless empty column with
 * a border down the side of it, which reads as data somebody forgot to fill in.
 *
 * Detected by contents, not by name or `dataIndex`: any column that holds only
 * row operations is an operations column, whoever authored the schema and
 * whatever they called it.
 */
const stripOperationColumns = (table: any) => {
  const columns = table?.items?.properties;
  if (!columns || typeof columns !== "object") return;
  Object.keys(columns).forEach((key) => {
    const column = columns[key];
    if (column?.["x-component"] !== "ArrayTable.Column") return;
    const inside = componentsIn(column).slice(1); // minus the column itself
    const isOperation = (c: string) =>
      ARRAY_OPERATIONS.includes(c) || c === "FormItem";
    if (inside.some((c) => ARRAY_OPERATIONS.includes(c)) && inside.every(isOperation)) {
      delete columns[key];
    }
  });
};

/**
 * The API returns saved attachments as `{id, name, url}`, but antd's Upload
 * list needs a `uid` (for its React keys) and a `status` to render an item as
 * an already-uploaded, clickable file rather than one still in flight.
 */
const normalizeFileValues = (values: any, fileFields: string[]) => {
  if (!values) return values;
  fileFields.forEach((fieldKey) => {
    const list = values[fieldKey];
    if (!Array.isArray(list)) return;
    values[fieldKey] = list.map((file: any) =>
      file && typeof file === "object" && file.uid === undefined
        ? { ...file, uid: String(file.id ?? file.name), status: "done" }
        : file
    );
  });
  return values;
};

/**
 * Clone the schema and:
 *  (a) collect the names of every Upload field at any depth and wire each Upload
 *      to the attachment endpoint with the bearer token;
 *  (b) apply per-form translations for the active language. Translations live on
 *      `schema['x-i18n'] = { kk: { "<English source>": "<translated>" }, zh: {…} }`
 *      and are looked up by the field/label/title's English source string, so
 *      they travel with the form in the DB and survive re-seeds. Missing entries
 *      fall back to the English source.
 */
const prepareSchema = (
  schema: any,
  token: string,
  fileFields: string[],
  lang?: string,
  readonly?: boolean
) => {
  const clone = JSON.parse(JSON.stringify(schema ?? {}));
  const dict =
    lang && lang !== "en" && clone["x-i18n"] && clone["x-i18n"][lang]
      ? clone["x-i18n"][lang]
      : null;
  const tr = (s: any) =>
    dict && typeof s === "string" && dict[s] != null ? dict[s] : s;

  const walk = (node: any) => {
    if (!node || typeof node !== "object") return;
    if (readonly && node["x-component"] === "ArrayTable") {
      stripOperationColumns(node);
    }
    if (node["x-component"] === "Upload" && node.name) {
      fileFields.push(node.name);
      node["x-component-props"] = {
        // `picture` rather than `text`: an attachment field is now as likely to
        // hold a photograph as a document, and a row of filenames says nothing
        // about whether the right thing was photographed. Non-images still get
        // their file icon.
        listType: "picture",
        multiple: true,
        ...(node["x-component-props"] || {}),
        action: ATTACHMENT_ACTION,
        name: "file",
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        onPreview: downloadAttachment,
        // Offer the camera only where the field can still be filled in. A
        // read-only permit is a record; there is nothing to add to it.
        photoCapture: !readonly,
      };
    }
    // Option groups carry a CSS-grid `style` so their checkboxes form even
    // columns. In readPretty formily swaps Checkbox.Group for PreviewText.Select,
    // which spreads that same style onto a plain <div> of <Tag>s — the tags then
    // become 190px grid items, and antd's `white-space: nowrap` makes a long
    // label overflow its column and collide with the next tag. The grid is a
    // checkbox-layout concern, so drop it and let the tags flow inline.
    if (readonly && OPTION_GROUPS.includes(node["x-component"])) {
      const cp = node["x-component-props"];
      if (cp?.style?.display === "grid") {
        const { display, gridTemplateColumns, gap, ...rest } = cp.style;
        cp.style = rest;
      }
    }
    if (dict) {
      if (typeof node.title === "string") node.title = tr(node.title);
      if (Array.isArray(node.enum)) {
        node.enum = node.enum.map((o: any) =>
          o && typeof o === "object" ? { ...o, label: tr(o.label) } : o
        );
      }
      const cp = node["x-component-props"];
      if (cp && typeof cp === "object") {
        if (typeof cp.title === "string") cp.title = tr(cp.title); // Card
        if (typeof cp.content === "string") cp.content = tr(cp.content); // Text
      }
    }
    if (node.properties && typeof node.properties === "object") {
      Object.values(node.properties).forEach(walk);
    }
    if (node.items) walk(node.items);
  };
  walk(clone);
  delete clone["x-i18n"];
  return clone;
};

type Props = {
  schema: any;
  formProps: any;
  formId?: number;
  requestId?: number;
  readonly?: boolean;
  values?: any;
  onSuccess: (result: any) => void;
  duplicateRequestId?: number | undefined;
  setDuplicateRequestId?: (duplicateRequestId: number | undefined) => void;
  system_form_extra_actions?: any;
  showPrev?: boolean;
  onPrev?: () => void;
  // When set (section owner on the View page), renders a "Submit" button next to
  // Save that saves the values first, then marks the section done.
  onSubmitSection?: () => Promise<void>;
  // Wording for that button — a section that carries its own signature line
  // says "Sign and submit", since that is what the click actually does.
  submitLabel?: string;
};

const FormView = ({
  schema,
  formProps,
  formId,
  requestId,
  readonly,
  values,
  onSuccess,
  duplicateRequestId,
  setDuplicateRequestId,
  system_form_extra_actions,
  showPrev,
  onPrev,
  onSubmitSection,
  submitLabel,
}: Props) => {
  const { i18n } = useTranslation();
  // Memoize the form per (formId, readonly) so incidental re-renders — e.g. a
  // token refresh or offline-sync tick fired when the browser tab regains focus
  // — don't recreate it and wipe the user's typed-in values. A new step (new
  // formId) still gets a fresh form.
  const form = useMemo(
    () => createForm({ readPretty: readonly }),
    [formId, readonly]
  );
  const [loading, setLoading] = useState<boolean>(false);
  // Prepare the schema: find every Upload field (any depth) and wire it to the
  // attachment endpoint with the bearer token; collect their names for submit.
  const fileFieldsRef = useRef<string[]>([]);
  const preparedSchema = useMemo(() => {
    const ff: string[] = [];
    const s = prepareSchema(
      schema,
      getAccessToken(),
      ff,
      i18n.language,
      readonly
    );
    fileFieldsRef.current = ff;
    return s;
  }, [schema, i18n.language, readonly]);
  const [nearbyWorksModalVisible, setNearbyWorksModalVisible] =
    useState<boolean>(false);
  const [startDate, setStartDate] = useState<string>("");
  const [endDate, setEndDate] = useState<string>("");
  const [location, setLocation] = useState<string>("");

  // Memoized so re-renders don't remount the whole field tree (which would drop
  // focus / in-progress input).
  const SchemaField = useMemo(
    () =>
      createSchemaField({
        scope: {
          $t: (key: string, options: any) => i18n.t(key, options),
        },
        components: {
          Form,
          FormItem,
          DatePicker,
          Checkbox,
          Cascader,
          Editable,
          Input,
          NumberPicker,
          Switch,
          Password,
          PreviewText,
          Radio,
          Reset,
          Select,
          Space,
          Submit,
          TimePicker,
          Transfer,
          TreeSelect,
          // The camera-capable uploader, registered under the plain name so
          // every stored schema picks it up untouched.
          Upload: UploadWithCamera,
          FormGrid,
          FormLayout,
          FormTab,
          FormCollapse,
          ArrayTable,
          ArrayCards,
          Card,
          Text,
          UserSelect,
          EntitySelect,
          // Formatted prose: an answer (RichText) and a block of guidance
          // (Markdown). An unregistered component renders as NOTHING -- the
          // field simply disappears from the form -- so both apps' maps have to
          // carry these, and the PWA's does.
          RichText: RichTextInput,
          Markdown,
        },
      }),
    [i18n]
  );

  // What each field starts as, for the person actually opening the form (C10).
  // Computed from the schema and the signed-in profile rather than stored, so a
  // "their own department" default is theirs and not whoever last edited the
  // template. Stored answers always win -- see `withDefaults`.
  const defaults = useMemo(
    () => collectDefaults(schema, currentProfile()),
    [schema]
  );

  useEffect(() => {
    form.setValues(withDefaults(values, defaults));
  }, [values, defaults, form]);

  const getFormValues = async (requestId?: number, form_id?: number) => {
    if (!requestId) return undefined;
    const result = await ApiService.apiMainRequestFormRetrieve(
      form_id!,
      requestId!
    );

    return normalizeFileValues(result.values, fileFieldsRef.current);
  };

  useEffect(() => {
    (async () => {
      if (duplicateRequestId === undefined) {
        console.log("test");
        form.setLoading(true);
        const data = await getFormValues(requestId, formId);
        form.setInitialValues(data);

        form.setLoading(false);
      } else if (requestId && duplicateRequestId) {
        const data = await getFormValues(requestId, formId);
        const hasValues = Object.keys(data).length > 0;
        if (hasValues) {
          form.setInitialValues(data);
        } else {
          const data = await getFormValues(duplicateRequestId, formId);
          form.setInitialValues(data);
        }
      }
    })();
  }, [form, formId, requestId]);

  useEffect(() => {
    (async () => {
      console.log("qwe");
      form.setLoading(true);
      const data = await getFormValues(duplicateRequestId, formId);
      form.setInitialValues(data);

      form.setLoading(false);
    })();
  }, [duplicateRequestId]);

  const handleSubmit = async (values: any) => {
    form.setSubmitting(true);
    setLoading(true);
    fileFieldsRef.current.forEach((fieldKey) => {
      const val = values[fieldKey];
      if (!Array.isArray(val)) return;
      values[fieldKey] = {
        type: "file",
        values: val
          .map((item: any) =>
            item?.response === undefined ? item?.id : item.response.id
          )
          .filter((id: any) => id !== undefined && id !== null),
      };
    });

    try {
      const result = await ApiService.apiMainRequestFormCreate({
        form_id: formId ?? 0,
        request_id: requestId ?? null,
        data: values,
      });

      openNotificationWithIcon("success", t("Form successfully saved"));
      onSuccess(result);
      const data = await getFormValues(result.request_id!, formId);
      form.setInitialValues(data);
    } catch (e) {
      // Say what the server said. A silent catch here is how a stage-gated
      // section became "I click Save and nothing happens".
      openNotificationWithIcon(
        "error",
        apiErrorMessage(e, t("The section could not be saved."))
      );
      throw e;
    } finally {
      // In `finally`, because the catch above rethrows: leaving these on the
      // happy path alone would strand the form in its submitting state after a
      // refusal, with the Save button spinning forever.
      setLoading(false);
      form.setSubmitting(false);
    }
  };

  // "Submit": save the values first (validates the form), then mark the section
  // done -- so a section can never be submitted empty.
  const handleSaveAndSubmit = async () => {
    try {
      await form.submit(handleSubmit);
      if (onSubmitSection) await onSubmitSection();
    } catch (e) {
      // Either Formily flagged the offending fields, or handleSubmit already
      // showed the server's refusal -- and in both cases the section must NOT
      // go on to be marked done.
    }
  };

  return (
    <>
      <Form
        form={form}
        {...formProps}
        onAutoSubmit={handleSubmit}
        // handleSubmit rethrows so Save-and-Submit can stop; Formily hands the
        // rejection here, and without a handler it surfaces as an unhandled
        // promise rejection in the console.
        onAutoSubmitFailed={() => {}}
      >
        <SchemaField schema={preparedSchema} />
        {!readonly && (
          <Row justify={showPrev ? "space-between" : "end"} align="middle">
            {showPrev && (
              <Col>
                <Button onClick={onPrev}>{t("Previous")}</Button>
              </Col>
            )}
            <Col>
              <ButtonGroup>
                {system_form_extra_actions &&
                  system_form_extra_actions.map((x: string) => {
                    if (x == "ViewNearbyWorks") {
                      return (
                        <Button
                          danger={true}
                          type="dashed"
                          loading={loading}
                          onClick={() => {
                            setNearbyWorksModalVisible(true);
                          }}
                        >
                          {t("View nearby works")}
                        </Button>
                      );
                    }
                  })}
                {/* minWidth, never width: these labels are translated, and
                    "Sign and submit" in any language is wider than 100px — a
                    fixed width clipped it. The floor keeps short labels
                    ("Save", "Submit") the same size as they always were. */}
                <Button
                  style={{ minWidth: 100 }}
                  type={onSubmitSection ? "default" : "primary"}
                  htmlType="submit"
                  loading={loading}
                >
                  {t("Save")}
                </Button>
                {onSubmitSection && (
                  <Button
                    style={{ minWidth: 100 }}
                    type="primary"
                    loading={loading}
                    onClick={handleSaveAndSubmit}
                  >
                    {submitLabel ?? t("Submit")}
                  </Button>
                )}
              </ButtonGroup>
            </Col>
          </Row>
        )}
      </Form>
      <NearbyWorksModal
        visible={nearbyWorksModalVisible}
        onClose={() => setNearbyWorksModalVisible(false)}
        start_date="01.01.2024"
        end_date="31.01.2024"
        location="planta"
      />
    </>
  );
};

export default FormView;
